import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { CenteredSpinner } from '../../components/ui/Spinner';
import type { PostQueue, PostQueueSlot } from '../../types/publishing';
import { createQueue, deleteQueue, listQueues, updateQueue } from './api';

// Publishing — queue/time-slots editor (Slice E). Per-profile weekly {dayOfWeek,
// time} slots on Zernio (queue.*). Add-to-queue in the composer drops content into
// the next open slot.

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

interface Draft {
  queueId: string | null; // null = new queue
  name: string;
  timezone: string;
  slots: PostQueueSlot[];
}

function draftFromQueue(q: PostQueue): Draft {
  return { queueId: q.queueId, name: q.name, timezone: q.timezone, slots: q.slots };
}
function newDraft(): Draft {
  return { queueId: null, name: '', timezone: browserTimezone(), slots: [{ dayOfWeek: 1, time: '09:00' }] };
}

export function QueueSlotsEditor({
  open,
  onClose,
  profileId,
}: {
  open: boolean;
  onClose: () => void;
  profileId: string;
}) {
  const qc = useQueryClient();
  const queues = useQuery({
    queryKey: ['publishing-queues', profileId],
    queryFn: () => listQueues(profileId),
    enabled: open,
  });

  const [draft, setDraft] = useState<Draft | null>(null);

  // Default to editing the first queue, or a fresh one, when the dialog opens.
  useEffect(() => {
    if (open && draft === null && queues.data) {
      setDraft(queues.data[0] ? draftFromQueue(queues.data[0]) : newDraft());
    }
    if (!open) setDraft(null);
  }, [open, draft, queues.data]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['publishing-queues', profileId] });

  const save = useMutation({
    mutationFn: async (d: Draft) => {
      if (d.queueId) {
        return updateQueue({ profileId, queueId: d.queueId, name: d.name, timezone: d.timezone, slots: d.slots });
      }
      return createQueue({ profileId, name: d.name, timezone: d.timezone, slots: d.slots });
    },
    onSuccess: () => {
      toast.success('Queue saved');
      invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not save the queue'),
  });

  const remove = useMutation({
    mutationFn: (queueId: string) => deleteQueue(profileId, queueId),
    onSuccess: () => {
      toast.success('Queue deleted');
      setDraft(newDraft());
      invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not delete the queue'),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Posting queue slots</DialogTitle>
        </DialogHeader>

        {queues.isLoading || !draft ? (
          <CenteredSpinner />
        ) : (
          <div className="space-y-4">
            {/* queue switcher */}
            {(queues.data?.length ?? 0) > 0 && (
              <div className="flex flex-wrap gap-1">
                {queues.data!.map((q) => (
                  <Button
                    key={q.queueId}
                    type="button"
                    size="sm"
                    variant={draft.queueId === q.queueId ? 'default' : 'outline'}
                    onClick={() => setDraft(draftFromQueue(q))}
                  >
                    {q.name}
                  </Button>
                ))}
                <Button
                  type="button"
                  size="sm"
                  variant={draft.queueId === null ? 'default' : 'ghost'}
                  onClick={() => setDraft(newDraft())}
                >
                  + New
                </Button>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <Input
                value={draft.name}
                placeholder="Queue name"
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
              <Input
                value={draft.timezone}
                placeholder="Timezone (IANA)"
                onChange={(e) => setDraft({ ...draft, timezone: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-500">Weekly slots</p>
              {draft.slots.map((slot, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <select
                    value={slot.dayOfWeek}
                    className="h-9 rounded-md border border-slate-200 px-2 text-sm"
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        slots: draft.slots.map((s, i) => (i === idx ? { ...s, dayOfWeek: Number(e.target.value) } : s)),
                      })
                    }
                  >
                    {DAYS.map((d, di) => (
                      <option key={di} value={di}>
                        {d}
                      </option>
                    ))}
                  </select>
                  <input
                    type="time"
                    value={slot.time}
                    className="h-9 rounded-md border border-slate-200 px-2 text-sm"
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        slots: draft.slots.map((s, i) => (i === idx ? { ...s, time: e.target.value } : s)),
                      })
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setDraft({ ...draft, slots: draft.slots.filter((_, i) => i !== idx) })}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDraft({ ...draft, slots: [...draft.slots, { dayOfWeek: 1, time: '09:00' }] })}
              >
                + Add slot
              </Button>
            </div>

            <div className="flex items-center justify-between pt-2">
              {draft.queueId ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-red-600"
                  onClick={() => remove.mutate(draft.queueId!)}
                  disabled={remove.isPending}
                >
                  Delete queue
                </Button>
              ) : (
                <span />
              )}
              <Button
                type="button"
                onClick={() => save.mutate(draft)}
                disabled={save.isPending || draft.name.trim() === '' || draft.slots.length === 0}
              >
                {save.isPending ? 'Saving…' : 'Save queue'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
